import { useQuery } from '@tanstack/react-query'
import { ticketConnectionQualityQuery, type CqVerdict } from '../api/connectionQuality'
import { CQ_COLORS, CqChart, type CqChartSeries } from '../devices/CqChart'
import { CompareLine, CqChecks } from '../devices/CqChecks'
import { CqTraceView } from '../devices/CqPath'
import { TicketSpeedLine } from '../devices/CqSpeed'
import { TicketWifiLine } from '../devices/CqWifiTicket'
import '../devices/DeviceConnectionQuality.css'
import '../devices/CqPath.css'
import { Section } from './TicketSummaryBlocks'

/**
 * "Connection when the ticket was opened" - in coloana din dreapta a
 * rezumatului, sub masinile tichetului.
 *
 * Cum mergea legatura aparatului in cele 30 de minute dinaintea tichetului:
 * latenta si pierderile pe cele trei linii, concluzia (si comparatia cu
 * sediul), ultimul traseu, verificarile. Serverul (TicketConnection.php) o
 * salveaza la prima deschidere, ca sa ramana si dupa ce expira datele brute.
 * Din faza 3 tine si ultima stare Wi-Fi (semnal, banda, AP, deconectari),
 * din faza 4 si ultimul test de viteza reusit din cele 7 zile dinainte.
 *
 * Sectiunea NU apare deloc cand tichetul n-are aparat cu Connection Quality,
 * cand omul n-are dreptul ecranului CQ, sau cand cererea pica - e o bucata in
 * plus a fisei, nu una fara de care tichetul nu se poate citi.
 */

const LINES = [
  { key: 'gateway', name: 'Router', color: CQ_COLORS[0] },
  { key: 'isp', name: 'Provider', color: CQ_COLORS[1] },
  { key: 'target', name: 'Target', color: CQ_COLORS[2] },
] as const

const TONE: Record<CqVerdict['code'], string> = {
  ok: 'is-ok',
  local: 'is-bad',
  isp: 'is-bad',
  target: 'is-bad',
  no_data: 'is-none',
}

export function TicketConnectionQuality({ id }: { id: number }) {
  const { data } = useQuery(ticketConnectionQualityQuery(id))
  if (!data || !data.available) return null

  const common = { from: data.from, to: data.to, bucketS: data.bucket_s }
  const latency: CqChartSeries[] = LINES.map((l) => ({
    name: l.name,
    color: l.color,
    points: data.latency[l.key].map((p) => ({ t: p.bucket, v: p.avg, lo: p.min, hi: p.max })),
  }))
  const loss: CqChartSeries[] = LINES.filter((l) => !(data.gatewaySilent && l.key === 'gateway')).map((l) => ({
    name: l.name,
    color: l.color,
    points: data.latency[l.key].map((p) => ({ t: p.bucket, v: p.loss_pct })),
  }))
  const verdict = data.verdict

  return (
    <div className="tk-connection-quality" data-ticket={id}>
      <Section title="Connection when the ticket was opened">
        <div className="cq-note">
          {data.device.name || `Device ${data.device.id}`} · {data.from} - {data.to}
          {data.source === 'snapshot' && data.taken_at ? ` · saved ${data.taken_at}` : ''}
        </div>
        <div className={`cq-verdict ${TONE[verdict.code] ?? 'is-none'}`} data-verdict={verdict.code}>
          <div>
            <div className="cq-verdict-text">{verdict.text}</div>
            <div className="cq-verdict-why">{verdict.why}</div>
            {(verdict.notes ?? []).map((n) => (
              <div key={n} className="cq-verdict-note">
                {n}
              </div>
            ))}
            {data.compare ? <CompareLine compare={data.compare} /> : null}
          </div>
        </div>
        {data.wifi ? <TicketWifiLine wifi={data.wifi} /> : null}
        {data.speed ? <TicketSpeedLine test={data.speed} /> : null}
        <CqChart title="Latency before the ticket" unit="ms" series={latency} height={180} {...common} />
        <CqChart title="Packet loss before the ticket" unit="%" series={loss} height={160} {...common} />
        {data.trace ? (
          <div className="cq-path">
            <h5>Last traceroute before the ticket</h5>
            <CqTraceView trace={data.trace} deviceName={data.device.name || undefined} />
          </div>
        ) : null}
        <CqChecks checks={data.checks} />
      </Section>
    </div>
  )
}
